import { useCallback, useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { Autocomplete, TextField } from "@mui/material";
import { useSnackbar } from "notistack";
import { DOMAIN_API_HOST } from "../../../configs/main";
import useAdminUser from "../../../data/useAdminUser";
import "./SspClicks.scss";

const REMOVE_ANY_EMAILS = ["a.tuhtaev@programmatic.ru", "e.usikova@programmatic.ru"];

function authHeaders() {
  return {
    "Content-Type": "application/json",
    Authorization: "Bearer " + localStorage.getItem("admin_token"),
  };
}

function errorDetail(data, fallback) {
  if (!data) return fallback;
  if (typeof data.detail === "string") return data.detail;
  if (Array.isArray(data.detail) && data.detail[0]?.msg) return data.detail[0].msg;
  return fallback;
}

function periodLabel(task) {
  if (!task.date_from || !task.date_to) return "Сразу";
  return `${task.date_from} — ${task.date_to}`;
}

const MONTHS = ["января", "февраля", "марта", "апреля", "мая", "июня", "июля", "августа", "сентября", "октября", "ноября", "декабря"];

function dayMonth(iso) {
  const match = String(iso || "").match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return "";
  return `${Number(match[3])} ${MONTHS[Number(match[2]) - 1]}`;
}

function dayStatus(task) {
  if (task.status === "done") return { kind: "done", title: "Готово", note: "" };
  if (task.today_complete && task.rest_days > 0) {
    const note = task.rest_days === 1 ? "осталось завтра" : `осталось до ${dayMonth(task.date_to)}`;
    return { kind: "today", title: "Сегодня готово", note };
  }
  if (task.today_complete) return { kind: "today", title: "Сегодня готово", note: "" };
  return { kind: "work", title: "В работе", note: "" };
}

function DayStatus({ task }) {
  const day = dayStatus(task);
  return (
    <>
      <span className={`SspClicks-status is-${day.kind === "work" ? "work" : "done"}`}>{day.title}</span>
      {day.note ? <span className="SspClicks-statusNote">{day.note}</span> : null}
    </>
  );
}

function adsCountLabel(count) {
  const value = Number(count) || 0;
  const mod100 = value % 100;
  const mod10 = value % 10;
  if (mod100 > 10 && mod100 < 20) return `${value} объявлений`;
  if (mod10 === 1) return `${value} объявление`;
  if (mod10 >= 2 && mod10 <= 4) return `${value} объявления`;
  return `${value} объявлений`;
}

function ruCount(count, one, few, many) {
  const value = Number(count) || 0;
  const formatted = value.toLocaleString("ru-RU");
  const mod100 = value % 100;
  const mod10 = value % 10;
  if (mod100 > 10 && mod100 < 20) return `${formatted} ${many}`;
  if (mod10 === 1) return `${formatted} ${one}`;
  if (mod10 >= 2 && mod10 <= 4) return `${formatted} ${few}`;
  return `${formatted} ${many}`;
}

function sectionStats(tasks, clicks) {
  return `${ruCount(tasks, "задача", "задачи", "задач")} · ${ruCount(clicks, "клик", "клика", "кликов")} в день`;
}

function periodDays(from, to) {
  if (!from || !to || from > to) return 0;
  const start = Date.parse(`${from}T00:00:00`);
  const end = Date.parse(`${to}T00:00:00`);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return 0;
  return Math.round((end - start) / 86400000) + 1;
}

function authorLabel(item) {
  const name = (item?.name || "").trim();
  const email = item?.email || "";
  return name ? `${name} · ${email}` : email;
}

function filterAuthors(options, state) {
  const raw = state.inputValue.trim().toLowerCase();
  if (!raw) return options;
  return options.filter((item) => {
    const email = (item.email || "").toLowerCase();
    const name = (item.name || "").toLowerCase();
    return email.includes(raw) || name.includes(raw);
  });
}

const FILTER_FIELD_SX = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    height: 36,
    minHeight: 36,
    padding: "0 12px !important",
    borderRadius: "4px",
    "& fieldset": { borderColor: "var(--sv-border)", top: 0 },
    "& fieldset legend": { display: "none" },
    "&:hover fieldset": { borderColor: "var(--sv-border)" },
    "&.Mui-focused fieldset": { borderColor: "var(--sv-border)", borderWidth: 1 },
  },
  "& .MuiOutlinedInput-root:has(.MuiChip-root)": {
    height: "auto",
    minHeight: 36,
    paddingTop: "4px !important",
    paddingBottom: "4px !important",
    flexWrap: "wrap",
  },
  "& .MuiOutlinedInput-input, & .MuiAutocomplete-input": {
    height: 36,
    minHeight: 36,
    padding: "0 !important",
    fontSize: 14,
    lineHeight: "36px",
    boxSizing: "border-box",
  },
  "& .MuiAutocomplete-endAdornment": { display: "none" },
  "& .MuiChip-root": { height: 24, margin: "4px 4px 4px 0" },
};

function Pager({ page, pages, onPage, loading }) {
  if (pages <= 1) return null;
  return (
    <div className={loading ? "SspClicks-pager is-loading" : "SspClicks-pager"}>
      <button type="button" className="SspClicks-fold" disabled={loading || page <= 1} onClick={() => onPage(page - 1)}>Назад</button>
      <span>{loading ? "Загрузка..." : `${page} из ${pages}`}</span>
      <button type="button" className="SspClicks-fold" disabled={loading || page >= pages} onClick={() => onPage(page + 1)}>Дальше</button>
    </div>
  );
}

function SspClicks() {
  const { user, isLoading } = useAdminUser();
  const email = (user?.email || "").toLowerCase();
  const allowed = user?.role === "SUPERUSER" || user?.role === "ADMIN";
  const canDebug = allowed;
  const canSeeLinks = allowed;
  const canRemoveAny = REMOVE_ANY_EMAILS.includes(email);
  const canPrioritize = canRemoveAny;
  const [errorsOn, setErrorsOn] = useState(false);
  const showErrors = canDebug && errorsOn;
  const { enqueueSnackbar } = useSnackbar();
  const [board, setBoard] = useState({ working: [], done: [] });
  const [loadError, setLoadError] = useState("");
  const [open, setOpen] = useState(false);
  const [linksTask, setLinksTask] = useState(null);
  const [removeAsk, setRemoveAsk] = useState(null);
  const [removing, setRemoving] = useState(false);
  const [priorityId, setPriorityId] = useState("");
  const [folded, setFolded] = useState({ queue: false, done: false });
  const [submitting, setSubmitting] = useState(false);
  const tasksAbort = useRef(null);
  const priorityLock = useRef(false);
  const [listQuery, setListQuery] = useState("");
  const listQueryRef = useRef("");
  const [authorOptions, setAuthorOptions] = useState([]);
  const [authors, setAuthors] = useState([]);
  const authorsRef = useRef([]);
  const queuePageRef = useRef(1);
  const donePageRef = useRef(1);
  const [queuePage, setQueuePage] = useState(1);
  const [donePage, setDonePage] = useState(1);
  const [queuePages, setQueuePages] = useState(1);
  const [donePages, setDonePages] = useState(1);
  const [queueClicks, setQueueClicks] = useState(0);
  const [doneClicks, setDoneClicks] = useState(0);
  const [queueTotal, setQueueTotal] = useState(0);
  const [doneTotal, setDoneTotal] = useState(0);
  const [paging, setPaging] = useState("");
  const pagingToken = useRef(0);
  const canSeeAuto = canRemoveAny;
  const [listSource, setListSource] = useState("manual");
  const sourceRef = useRef("manual");
  const [listBrand, setListBrand] = useState("pg");
  const brandRef = useRef("pg");
  const [clientQuery, setClientQuery] = useState("");
  const [clientOptions, setClientOptions] = useState([]);
  const [client, setClient] = useState(null);
  const [clientPack, setClientPack] = useState(null);
  const [planRows, setPlanRows] = useState({});
  const [loadingClient, setLoadingClient] = useState(false);
  const [editTask, setEditTask] = useState(null);
  const [editClicks, setEditClicks] = useState("10");
  const [editMode, setEditMode] = useState("day");
  const [editDateFrom, setEditDateFrom] = useState("");
  const [editDateTo, setEditDateTo] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);

  const loadBoard = useCallback(async (section) => {
    if (priorityLock.current) return;
    const token = section ? pagingToken.current + 1 : 0;
    if (section) {
      pagingToken.current = token;
      setPaging(section);
    }
    if (tasksAbort.current) tasksAbort.current.abort();
    const controller = new AbortController();
    tasksAbort.current = controller;
    const params = new URLSearchParams({
      queue_page: String(queuePageRef.current),
      done_page: String(donePageRef.current),
    });
    if (listQueryRef.current) params.set("q", listQueryRef.current);
    params.set("source", sourceRef.current || "manual");
    if (brandRef.current) params.set("brand", brandRef.current);
    if (sourceRef.current !== "auto") {
      (authorsRef.current || []).forEach((email) => {
        if (email) params.append("author", email);
      });
    }
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/tasks?${params}`, {
        headers: authHeaders(),
        signal: controller.signal,
      });
      const data = await res.json().catch(() => null);
      if (controller.signal.aborted || priorityLock.current) return;
      if (!res.ok) {
        setLoadError(errorDetail(data, "Не удалось загрузить задачи"));
        return;
      }
      setLoadError("");
      setBoard({ working: data.working || [], done: data.done || [] });
      const nextQueue = data.queue_page || 1;
      const nextDone = data.done_page || 1;
      queuePageRef.current = nextQueue;
      donePageRef.current = nextDone;
      setQueuePage(nextQueue);
      setDonePage(nextDone);
      setQueuePages(data.queue_pages || 1);
      setDonePages(data.done_pages || 1);
      setQueueClicks(data.queue_clicks || 0);
      setDoneClicks(data.done_clicks || 0);
      setQueueTotal(data.queue_total || 0);
      setDoneTotal(data.done_total || 0);
    } catch (err) {
      if (err?.name === "AbortError") return;
    } finally {
      if (section && pagingToken.current === token) setPaging("");
    }
  }, []);

  const openQueuePage = (page) => {
    if (paging) return;
    queuePageRef.current = page;
    setQueuePage(page);
    loadBoard("queue");
  };

  const openDonePage = (page) => {
    if (paging) return;
    donePageRef.current = page;
    setDonePage(page);
    loadBoard("done");
  };

  const openSource = (next) => {
    if (sourceRef.current === next) return;
    sourceRef.current = next;
    setListSource(next);
    if (next === "auto") {
      brandRef.current = "retargeo";
      setListBrand("retargeo");
    }
    queuePageRef.current = 1;
    donePageRef.current = 1;
    setQueuePage(1);
    setDonePage(1);
    loadBoard();
  };

  const openBrand = (next) => {
    if (brandRef.current === next) return;
    brandRef.current = next;
    setListBrand(next);
    queuePageRef.current = 1;
    donePageRef.current = 1;
    setQueuePage(1);
    setDonePage(1);
    loadBoard();
  };

  useEffect(() => {
    if (!allowed) return undefined;
    loadBoard();
    const timer = setInterval(loadBoard, 60000);
    return () => clearInterval(timer);
  }, [allowed, loadBoard]);

  const listQueryReady = useRef(false);
  useEffect(() => {
    if (!allowed) return undefined;
    if (!listQueryReady.current) {
      listQueryReady.current = true;
      return undefined;
    }
    const handle = setTimeout(() => {
      listQueryRef.current = listQuery.trim();
      queuePageRef.current = 1;
      donePageRef.current = 1;
      setQueuePage(1);
      setDonePage(1);
      loadBoard();
    }, 300);
    return () => clearTimeout(handle);
  }, [allowed, listQuery, loadBoard]);

  const authorsReady = useRef(false);
  useEffect(() => {
    authorsRef.current = authors.map((item) => item.email).filter(Boolean);
    if (!allowed) return undefined;
    if (!authorsReady.current) {
      authorsReady.current = true;
      return undefined;
    }
    queuePageRef.current = 1;
    donePageRef.current = 1;
    setQueuePage(1);
    setDonePage(1);
    loadBoard();
    return undefined;
  }, [allowed, authors, loadBoard]);

  useEffect(() => {
    if (!allowed) return undefined;
    let cancelled = false;
    (async () => {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/options`, { headers: authHeaders() });
      const data = await res.json().catch(() => null);
      if (!cancelled && res.ok) setAuthorOptions(Array.isArray(data?.authors) ? data.authors : []);
    })();
    return () => {
      cancelled = true;
    };
  }, [allowed]);

  useEffect(() => {
    if (!open) return undefined;
    const text = clientQuery.trim();
    if (text.length < 2) {
      setClientOptions([]);
      return undefined;
    }
    const timer = setTimeout(async () => {
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/clients?q=${encodeURIComponent(text)}`,
        { headers: authHeaders() }
      );
      const data = await res.json().catch(() => null);
      if (res.ok && Array.isArray(data)) setClientOptions(data);
    }, 300);
    return () => clearTimeout(timer);
  }, [clientQuery, open]);

  useEffect(() => {
    if (!open || !client?.id) {
      setClientPack(null);
      setPlanRows({});
      return undefined;
    }
    let cancelled = false;
    setLoadingClient(true);
    (async () => {
      const res = await fetch(
        `${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/clients/${client.id}/campaigns`,
        { headers: authHeaders() }
      );
      const data = await res.json().catch(() => null);
      if (cancelled) return;
      setLoadingClient(false);
      if (!res.ok) {
        setClientPack(null);
        setPlanRows({});
        enqueueSnackbar(errorDetail(data, "Не удалось загрузить кампании"), { variant: "error" });
        return;
      }
      setClientPack(data);
      const next = {};
      (data.campaigns || []).forEach((item) => {
        next[item.id] = {
          selected: Boolean(item.ready),
          clicks: "10",
          mode: "day",
          dateFrom: item.date_start || "",
          dateTo: item.date_end || "",
        };
      });
      setPlanRows(next);
    })();
    return () => {
      cancelled = true;
    };
  }, [client, open, enqueueSnackbar]);

  if (isLoading) return null;
  if (user && !allowed) return <Navigate to="/admin/clients" />;

  const closeModal = () => {
    setOpen(false);
    setClient(null);
    setClientQuery("");
    setClientOptions([]);
    setClientPack(null);
    setPlanRows({});
  };

  const updatePlanRow = (id, patch) => {
    setPlanRows((prev) => ({ ...prev, [id]: { ...prev[id], ...patch } }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!client?.id) {
      enqueueSnackbar("Выберите клиента", { variant: "warning" });
      return;
    }
    const campaigns = clientPack?.campaigns || [];
    const items = [];
    for (const campaign of campaigns) {
      const row = planRows[campaign.id];
      if (!row?.selected) continue;
      const count = Number(row.clicks);
      if (!campaign.ready) {
        enqueueSnackbar(`«${campaign.name}»: нет объявлений или гео`, { variant: "warning" });
        return;
      }
      if (!Number.isInteger(count) || count < 1) {
        enqueueSnackbar(`«${campaign.name}»: укажите клики`, { variant: "warning" });
        return;
      }
      if (row.mode === "cycle" && (!row.dateFrom || !row.dateTo)) {
        enqueueSnackbar(`«${campaign.name}»: для цикла нужны даты`, { variant: "warning" });
        return;
      }
      if (row.mode === "day" && count > 10000) {
        enqueueSnackbar(`«${campaign.name}»: не больше 10000 кликов в день`, { variant: "warning" });
        return;
      }
      if (row.mode === "cycle") {
        const days = periodDays(row.dateFrom, row.dateTo);
        const perDay = days ? Math.floor(count / days) : 0;
        if (perDay < 1) {
          enqueueSnackbar(`«${campaign.name}»: кликов меньше, чем дней`, { variant: "warning" });
          return;
        }
        if (perDay > 10000) {
          enqueueSnackbar(`«${campaign.name}»: больше 10000 кликов в день`, { variant: "warning" });
          return;
        }
      }
      if ((row.dateFrom && !row.dateTo) || (!row.dateFrom && row.dateTo)) {
        enqueueSnackbar(`«${campaign.name}»: укажите обе даты`, { variant: "warning" });
        return;
      }
      if (row.dateFrom && row.dateTo && row.dateFrom > row.dateTo) {
        enqueueSnackbar(`«${campaign.name}»: дата начала позже окончания`, { variant: "warning" });
        return;
      }
      items.push({
        campaign_id: campaign.id,
        clicks: count,
        clicks_mode: row.mode,
        date_from: row.dateFrom || null,
        date_to: row.dateTo || null,
      });
    }
    if (!items.length) {
      enqueueSnackbar("Выберите хотя бы одну кампанию", { variant: "warning" });
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/tasks/client-plan`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ client_id: client.id, items }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось создать план"), { variant: "error" });
        return;
      }
      const made = (data?.created || []).length;
      const failed = (data?.errors || []).length;
      if (failed) {
        enqueueSnackbar(`Создано ${made}, с ошибками: ${failed}`, { variant: "warning" });
      } else {
        enqueueSnackbar(made === 1 ? "План создан" : `Создано задач: ${made}`, { variant: "success" });
      }
      closeModal();
      await loadBoard();
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (task) => {
    const mode = task.clicks_mode === "cycle" ? "cycle" : "day";
    setEditTask(task);
    setEditMode(mode);
    setEditClicks(String(
      mode === "cycle"
        ? (task.clicks_cycle_plan || task.clicks_per_day || "")
        : (task.clicks_day_plan || task.clicks_per_day || "")
    ));
    setEditDateFrom(task.date_from || "");
    setEditDateTo(task.date_to || "");
  };

  const closeEdit = () => {
    if (savingEdit) return;
    setEditTask(null);
  };

  const saveEdit = async (event) => {
    event.preventDefault();
    if (!editTask) return;
    const count = Number(editClicks);
    if (!Number.isInteger(count) || count < 1) {
      enqueueSnackbar(editMode === "cycle" ? "Укажите клики за цикл" : "Укажите клики в день", { variant: "warning" });
      return;
    }
    if (editMode === "cycle" && (!editDateFrom || !editDateTo)) {
      enqueueSnackbar("Для цикла укажите даты", { variant: "warning" });
      return;
    }
    if (editMode === "day" && count > 10000) {
      enqueueSnackbar("Не больше 10000 кликов в день", { variant: "warning" });
      return;
    }
    if (editMode === "cycle") {
      const days = periodDays(editDateFrom, editDateTo);
      const perDay = days ? Math.floor(count / days) : 0;
      if (perDay < 1) {
        enqueueSnackbar("Кликов меньше, чем дней в периоде", { variant: "warning" });
        return;
      }
      if (perDay > 10000) {
        enqueueSnackbar("Получается больше 10000 кликов в день", { variant: "warning" });
        return;
      }
    }
    if ((editDateFrom && !editDateTo) || (!editDateFrom && editDateTo)) {
      enqueueSnackbar("Укажите обе даты или оставьте обе пустыми", { variant: "warning" });
      return;
    }
    if (editDateFrom && editDateTo && editDateFrom > editDateTo) {
      enqueueSnackbar("Дата начала позже даты окончания", { variant: "warning" });
      return;
    }
    setSavingEdit(true);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/tasks/${editTask.id}`, {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({
          clicks: count,
          clicks_mode: editMode,
          update_dates: true,
          date_from: editDateFrom || null,
          date_to: editDateTo || null,
        }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось сохранить"), { variant: "error" });
        return;
      }
      enqueueSnackbar("План обновлён", { variant: "success" });
      setEditTask(null);
      await loadBoard();
    } finally {
      setSavingEdit(false);
    }
  };

  const canEditTask = (task) => {
    if ((task.created_by || "").toLowerCase() === "auto") return canRemoveAny;
    return canRemoveAny || (task.created_by || "").toLowerCase() === email;
  };

  const canRemove = (task) => canRemoveAny || (task.created_by || "").toLowerCase() === email;

  const removeTask = async () => {
    if (!removeAsk || removing) return;
    setRemoving(true);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/tasks/${removeAsk.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось убрать задачу"), { variant: "error" });
        return;
      }
      setRemoveAsk(null);
      await loadBoard();
    } finally {
      setRemoving(false);
    }
  };

  const togglePriority = async (task) => {
    if (!canPrioritize || priorityLock.current) return;
    priorityLock.current = true;
    if (tasksAbort.current) tasksAbort.current.abort();
    setPriorityId(task.id);
    try {
      const res = await fetch(`${DOMAIN_API_HOST}/api/v1/admin/ssp-clicks/tasks/${task.id}/priority`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({ priority: !task.priority }),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) {
        enqueueSnackbar(errorDetail(data, "Не удалось поставить приоритет"), { variant: "error" });
        return;
      }
      if (data?.priority) {
        queuePageRef.current = 1;
        setQueuePage(1);
      }
      setBoard((prev) => ({
        ...prev,
        working: prev.working.map((item) => (
          item.id === task.id ? { ...item, priority: Boolean(data?.priority) } : item
        )),
      }));
    } finally {
      priorityLock.current = false;
      setPriorityId("");
      loadBoard();
    }
  };

  const renderTable = (rows, empty, allowRemove, allowPriority) => (
    rows.length === 0 ? (
      <p className="SspClicks-empty">{empty}</p>
    ) : (
      <table className="SspClicks-table">
        <thead>
          <tr>
            <th>Кампания</th>
            <th>Клики</th>
            <th>Гео</th>
            <th>Период</th>
            <th>Кто поставил</th>
            {canSeeLinks ? <th>Ссылка</th> : null}
            <th>Статус</th>
            <th></th>
            {allowPriority ? <th></th> : null}
            {allowRemove ? <th></th> : null}
          </tr>
        </thead>
        <tbody>
          {rows.map((task) => (
            <tr key={task.id} className={allowPriority && task.priority ? "SspClicks-priority" : undefined}>
              <td>
                {task.campaign_name}
                {task.advertisement_name ? <span className="SspClicks-sub">{task.advertisement_name}</span> : null}
                {showErrors ? (task.warnings || []).filter((text) => (
                  text !== "Кликов меньше, чем баннеров: часть баннеров не получит визиты"
                  && text !== "Кликов в день меньше, чем выбранных городов"
                )).map((text) => (
                  <span key={text} className="SspClicks-problem">{text}</span>
                )) : null}
                {showErrors && task.problem ? <span className="SspClicks-problem">{task.problem}</span> : null}
              </td>
              <td>
                <span className="SspClicks-clicksCell">
                  <span>{task.clicks_day_done ?? 0} / {task.clicks_day_plan ?? task.clicks_per_day} в день</span>
                  {task.show_cycle !== false ? (
                    <span className="SspClicks-sub">{task.clicks_cycle_done ?? 0} / {task.clicks_cycle_plan ?? task.clicks_per_day} за цикл</span>
                  ) : null}
                </span>
              </td>
              <td className="SspClicks-geo"><span>{(task.geos || []).join(", ")}</span></td>
              <td>{periodLabel(task)}</td>
              <td>
                {task.created_by_name || task.created_by || "—"}
                {task.created_by_name && task.created_by && task.created_by.toLowerCase() !== "auto" ? <span className="SspClicks-sub">{task.created_by}</span> : null}
              </td>
              {canSeeLinks ? (
                <td>
                  {(task.links || []).length ? (
                    <button type="button" className="SspClicks-fold" onClick={() => setLinksTask(task)}>Ссылка</button>
                  ) : "—"}
                </td>
              ) : null}
              <td>
                <DayStatus task={task} />
              </td>
              <td>
                {canEditTask(task) ? (
                  <button type="button" className="SspClicks-fold" onClick={() => openEdit(task)}>Изменить</button>
                ) : null}
              </td>
              {allowPriority ? (
                <td>
                  <button
                    type="button"
                    className={task.priority ? "SspClicks-priorityBtn is-on" : "SspClicks-priorityBtn"}
                    disabled={priorityId === task.id}
                    onClick={() => togglePriority(task)}
                  >
                    {task.priority ? "В приоритете" : "Приоритет"}
                  </button>
                </td>
              ) : null}
              {allowRemove ? (
                <td>
                  {canRemove(task) ? (
                    <button type="button" className="SspClicks-remove" onClick={() => setRemoveAsk(task)}>Убрать</button>
                  ) : null}
                </td>
              ) : null}
            </tr>
          ))}
        </tbody>
      </table>
    )
  );

  return (
    <div className="SspClicks">
      <div className="SspClicks-header">
        <div>
          <h1>SSP Клики</h1>
          <p>
            Сетка на клики по метке PRG100
          </p>
        </div>
        <div className="SspClicks-headerActions">
          {listSource === "auto" ? null : <button type="button" onClick={() => setOpen(true)}>Добавить</button>}
        </div>
      </div>

      <div className="SspClicks-tabBars">
        {canSeeAuto ? (
          <div className="SspClicks-tabs" role="tablist" aria-label="Тип задач">
            <button type="button" role="tab" aria-selected={listSource === "manual"} className={listSource === "manual" ? "SspClicks-tab is-active" : "SspClicks-tab"} onClick={() => openSource("manual")}>Ручные</button>
            <button type="button" role="tab" aria-selected={listSource === "auto"} className={listSource === "auto" ? "SspClicks-tab is-active" : "SspClicks-tab"} onClick={() => openSource("auto")}>Авто</button>
          </div>
        ) : null}
        <div className="SspClicks-tabs" role="tablist" aria-label="Площадка">
          <button type="button" role="tab" aria-selected={listBrand === "pg"} className={listBrand === "pg" ? "SspClicks-tab is-active" : "SspClicks-tab"} onClick={() => openBrand("pg")}>ПГ</button>
          <button type="button" role="tab" aria-selected={listBrand === "retargeo"} className={listBrand === "retargeo" ? "SspClicks-tab is-active" : "SspClicks-tab"} onClick={() => openBrand("retargeo")}>Ретаргео</button>
        </div>
      </div>

      <div className={listSource === "auto" ? "SspClicks-filters is-single" : "SspClicks-filters"}>
        <label className="SspClicks-search">
          Поиск
          <TextField
            size="small"
            fullWidth
            sx={FILTER_FIELD_SX}
            value={listQuery}
            onChange={(event) => setListQuery(event.target.value)}
            placeholder="Кампания, почта клиента, сайт или кто поставил"
          />
        </label>
        {listSource === "auto" ? null : (
          <label className="SspClicks-authors">
            Кто поставил
            <Autocomplete
              multiple
              sx={FILTER_FIELD_SX}
              options={authorOptions}
              value={authors}
              onChange={(_, next) => setAuthors(next)}
              getOptionLabel={authorLabel}
              isOptionEqualToValue={(option, value) => option.email === value.email}
              filterOptions={filterAuthors}
              openOnFocus
              forcePopupIcon={false}
              disableClearable
              noOptionsText="Никого не найдено"
              ListboxProps={{ style: { maxHeight: 320 } }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  placeholder={authors.length ? "" : "Почта, фамилия или имя"}
                />
              )}
            />
          </label>
        )}
      </div>

      {loadError ? <div className="SspClicks-error">{loadError}</div> : null}

      <section className="SspClicks-panel">
        <div className="SspClicks-panelHead">
          <h2>
            Очередь
            <span>{sectionStats(queueTotal, queueClicks)}</span>
          </h2>
          <button type="button" className="SspClicks-fold" onClick={() => setFolded((prev) => ({ ...prev, queue: !prev.queue }))}>
            {folded.queue ? "Показать" : "Скрыть"}
          </button>
        </div>
        {folded.queue ? null : (
          <>
            <div className={paging === "queue" ? "SspClicks-panelBody is-loading" : "SspClicks-panelBody"}>{renderTable(board.working, "Задач в работе нет", true, canPrioritize)}</div>
            <Pager page={queuePage} pages={queuePages} onPage={openQueuePage} loading={paging === "queue"} />
          </>
        )}
      </section>

      <section className="SspClicks-panel">
        <div className="SspClicks-panelHead">
          <h2>
            Завершённые
            <span>{sectionStats(doneTotal, doneClicks)}</span>
          </h2>
          <button type="button" className="SspClicks-fold" onClick={() => setFolded((prev) => ({ ...prev, done: !prev.done }))}>
            {folded.done ? "Показать" : "Скрыть"}
          </button>
        </div>
        {folded.done ? null : (
          <>
            <div className={paging === "done" ? "SspClicks-panelBody is-loading" : "SspClicks-panelBody"}>{renderTable(board.done, "Завершённых задач нет", canRemoveAny, false)}</div>
            <Pager page={donePage} pages={donePages} onPage={openDonePage} loading={paging === "done"} />
          </>
        )}
      </section>

      {removeAsk ? (
        <div className="SspClicks-modal" onClick={() => { if (!removing) setRemoveAsk(null); }}>
          <div className="SspClicks-dialog SspClicks-linkDialog" onClick={(event) => event.stopPropagation()}>
            <div className="SspClicks-dialogHead">
              <h2>Убрать задачу</h2>
              <button type="button" className="SspClicks-ghost" aria-label="Закрыть" disabled={removing} onClick={() => setRemoveAsk(null)}>×</button>
            </div>
            <p className="SspClicks-fullLink">Убрать «{removeAsk.campaign_name}»? Кампания будет убрана из очереди.</p>
            <div className="SspClicks-confirmActions">
              <button type="button" className="SspClicks-fold" disabled={removing} onClick={() => setRemoveAsk(null)}>Отмена</button>
              <button type="button" className="SspClicks-remove" disabled={removing} onClick={removeTask}>{removing ? "Убираю..." : "Убрать"}</button>
            </div>
          </div>
        </div>
      ) : null}

      {linksTask ? (
        <div className="SspClicks-modal" onClick={() => setLinksTask(null)}>
          <div className="SspClicks-dialog SspClicks-linkDialog" onClick={(event) => event.stopPropagation()}>
            <div className="SspClicks-dialogHead">
              <h2>Ссылка</h2>
              <button type="button" className="SspClicks-ghost" aria-label="Закрыть" onClick={() => setLinksTask(null)}>×</button>
            </div>
            {(linksTask.links || []).map((url) => (
              <p key={url} className="SspClicks-fullLink">{url}</p>
            ))}
          </div>
        </div>
      ) : null}

      {open ? (
        <div className="SspClicks-modal">
          <form className="SspClicks-dialog SspClicks-planDialog" onSubmit={submit}>
            <div className="SspClicks-dialogHead">
              <h2>План по клиенту</h2>
              <button type="button" className="SspClicks-ghost" aria-label="Закрыть" onClick={closeModal}>×</button>
            </div>
            <section className="SspClicks-block">
              <div className="SspClicks-blockTitle">Клиент</div>
              <label>
                Почта или юрлицо
                <Autocomplete
                  options={clientOptions}
                  value={client}
                  onChange={(_, next) => setClient(next)}
                  onInputChange={(_, value) => setClientQuery(value)}
                  getOptionLabel={(option) => {
                    const mail = option.email || "";
                    const legal = (option.legal_entity || "").trim();
                    return legal ? `${mail} · ${legal}` : mail;
                  }}
                  isOptionEqualToValue={(option, value) => option.id === value.id}
                  filterOptions={(options) => options}
                  noOptionsText={clientQuery.trim().length < 2 ? "Начните вводить почту или юрлицо" : "Ничего не найдено"}
                  renderInput={(params) => <TextField {...params} placeholder="email или юрлицо" />}
                />
              </label>
              <p className="SspClicks-hint">По активным РК работают все включённые объявления. Гео берётся из сегмента.</p>
            </section>
            <section className="SspClicks-block">
              <div className="SspClicks-blockTitle">Активные кампании</div>
              {!client ? (
                <p className="SspClicks-empty">Сначала выберите клиента</p>
              ) : loadingClient ? (
                <p className="SspClicks-empty">Загружаю кампании...</p>
              ) : !(clientPack?.campaigns || []).length ? (
                <p className="SspClicks-empty">Активных кампаний нет</p>
              ) : (
                <div className="SspClicks-planList">
                  {(clientPack.campaigns || []).map((campaign) => {
                    const row = planRows[campaign.id] || {
                      selected: false,
                      clicks: "10",
                      mode: "day",
                      dateFrom: "",
                      dateTo: "",
                    };
                    const showCycle = clientPack.show_cycle !== false;
                    return (
                      <div
                        key={campaign.id}
                        className={campaign.ready ? (row.selected ? "SspClicks-planCard is-on" : "SspClicks-planCard") : "SspClicks-planCard is-disabled"}
                      >
                        <label className="SspClicks-planCheck">
                          <input
                            type="checkbox"
                            checked={Boolean(row.selected)}
                            disabled={!campaign.ready}
                            onChange={(event) => updatePlanRow(campaign.id, { selected: event.target.checked })}
                          />
                          <span>
                            <strong>{campaign.name}</strong>
                            <em>
                              {adsCountLabel(campaign.active_ads)}
                              {(campaign.geos || []).length ? ` · ${(campaign.geos || []).map((item) => item.label).join(", ")}` : " · нет гео"}
                            </em>
                            {!campaign.ready ? (
                              <i className="SspClicks-problem">Нужны включённые объявления и гео в сегменте</i>
                            ) : null}
                          </span>
                        </label>
                        <div className="SspClicks-planFields">
                          <div className="SspClicks-planField">
                            <span>Лимит</span>
                            <div className="SspClicks-mode is-compact">
                              <button
                                type="button"
                                className={row.mode === "day" ? "is-on" : undefined}
                                disabled={!row.selected}
                                onClick={() => updatePlanRow(campaign.id, { mode: "day" })}
                              >
                                Сутки
                              </button>
                              {showCycle ? (
                                <button
                                  type="button"
                                  className={row.mode === "cycle" ? "is-on" : undefined}
                                  disabled={!row.selected}
                                  onClick={() => updatePlanRow(campaign.id, { mode: "cycle" })}
                                >
                                  Цикл
                                </button>
                              ) : null}
                            </div>
                          </div>
                          <div className="SspClicks-planField">
                            <span>{row.mode === "cycle" ? "Клики за цикл" : "Клики в сутки"}</span>
                            <input
                              type="number"
                              min="1"
                              max={row.mode === "cycle" ? "300000" : "10000"}
                              value={row.clicks}
                              disabled={!row.selected}
                              onChange={(event) => updatePlanRow(campaign.id, { clicks: event.target.value })}
                            />
                          </div>
                          <div className="SspClicks-planField SspClicks-planFieldDates">
                            <span>Даты</span>
                            <div className="SspClicks-dates is-compact">
                              <input
                                type="date"
                                value={row.dateFrom}
                                disabled={!row.selected}
                                onChange={(event) => updatePlanRow(campaign.id, { dateFrom: event.target.value })}
                              />
                              <input
                                type="date"
                                value={row.dateTo}
                                disabled={!row.selected}
                                onChange={(event) => updatePlanRow(campaign.id, { dateTo: event.target.value })}
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>
            <button type="submit" disabled={submitting || !client || loadingClient}>
              {submitting ? "Создаю..." : "Создать план"}
            </button>
          </form>
        </div>
      ) : null}

      {editTask ? (
        <div className="SspClicks-modal" onClick={closeEdit}>
          <form className="SspClicks-dialog" onClick={(event) => event.stopPropagation()} onSubmit={saveEdit}>
            <div className="SspClicks-dialogHead">
              <h2>Изменить план</h2>
              <button type="button" className="SspClicks-ghost" aria-label="Закрыть" disabled={savingEdit} onClick={closeEdit}>×</button>
            </div>
            <p className="SspClicks-fullLink">{editTask.campaign_name}</p>
            <section className="SspClicks-block">
              <div className="SspClicks-blockHead">
                <div className="SspClicks-blockTitle">Лимит</div>
                <div className="SspClicks-mode">
                  <button type="button" className={editMode === "day" ? "is-on" : undefined} onClick={() => setEditMode("day")}>Сутки</button>
                  {editTask.show_cycle !== false ? (
                    <button type="button" className={editMode === "cycle" ? "is-on" : undefined} onClick={() => setEditMode("cycle")}>Цикл</button>
                  ) : null}
                </div>
              </div>
              <label>
                {editMode === "cycle" ? "Кликов за цикл" : "Кликов в день"}
                <input
                  type="number"
                  min="1"
                  max={editMode === "cycle" ? "300000" : "10000"}
                  value={editClicks}
                  onChange={(event) => setEditClicks(event.target.value)}
                />
              </label>
              <div className="SspClicks-dates">
                <label>
                  Дата с
                  <input type="date" value={editDateFrom} onChange={(event) => setEditDateFrom(event.target.value)} />
                </label>
                <label>
                  Дата по
                  <input type="date" value={editDateTo} onChange={(event) => setEditDateTo(event.target.value)} />
                </label>
              </div>
              <p className="SspClicks-hint">Если цикл закончился, продлите даты — задача вернётся в очередь.</p>
            </section>
            <button type="submit" disabled={savingEdit}>
              {savingEdit ? "Сохраняю..." : "Сохранить"}
            </button>
          </form>
        </div>
      ) : null}

      {canDebug ? (
        <button
          type="button"
          className={errorsOn ? "SspClicks-debug is-on" : "SspClicks-debug"}
          onClick={() => setErrorsOn((value) => !value)}
        >
          {errorsOn ? "Скрыть ошибки" : "Ошибки"}
        </button>
      ) : null}
    </div>
  );
}

export default SspClicks;
